import React from "react";
import { Repeat, ChevronRight } from "lucide-react";
import { fmtMoney } from "@/lib/currency";

/**
 * "Upcoming Recurring — Next 60 Days" preview.
 *
 * Renders items returned from GET /api/recurring/upcoming — these are
 * previews only. Nothing is counted in Spent/Revenue/Outstanding until each
 * occurrence date is materialised by the backend catch-up job.
 *
 * Clicking a row asks the parent to open the rule editor.
 */
export default function RecurringUpcomingPanel({ items = [], projects = [], onOpenRule }) {
  if (!items || items.length === 0) return null;
  const nameOf = (pid) => projects.find(p => p.id === pid)?.name || "Entity-level";

  const cadenceLabel = (c) => {
    const v = (c || "monthly").toLowerCase();
    if (v === "annually") return "Annual";
    if (v === "quarterly") return "Quarterly";
    return "Monthly";
  };

  return (
    <div
      className="mb-6 p-4 rounded-xl border border-white/10 bg-[#151A23]"
      data-testid="upcoming-recurring-panel"
    >
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Repeat className="w-3.5 h-3.5 text-white/60" />
          <div className="text-eyebrow">Upcoming Recurring — Next 60 Days</div>
        </div>
        <div className="text-[11px] text-white/40">
          Preview only. Not counted in totals until the date arrives.
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((u, i) => {
          const isRev = (u.kind || "expense").toLowerCase() === "revenue";
          const counterparty = isRev ? (u.source_client || "") : (u.vendor || "");
          const counterpartyLabel = isRev ? "Client" : "Vendor";
          return (
            <button
              key={`${u.rule_id}-${u.date}-${i}`}
              onClick={() => onOpenRule?.(u.rule_id)}
              data-testid={`upcoming-rec-row-${u.rule_id}-${u.date}`}
              className="w-full grid grid-cols-[auto_1fr_auto] items-center gap-3 p-2.5 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.06] transition text-left"
            >
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[10px] uppercase tracking-wider text-white/40">Date</span>
                <span className="text-sm text-white/85 tabular-nums">{u.date}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                    style={{
                      color: isRev ? "#10B981" : "#EF4444",
                      borderColor: `${isRev ? "#10B981" : "#EF4444"}55`,
                      background: `${isRev ? "#10B981" : "#EF4444"}12`,
                    }}
                  >
                    {isRev ? "REVENUE" : "EXPENSE"}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/15 text-white/60">
                    {cadenceLabel(u.cadence)}
                  </span>
                  <span className="text-xs text-white/50 truncate">{nameOf(u.project_id)}</span>
                </div>
                <div className="text-sm text-white/90 truncate mt-0.5">
                  {u.description || (isRev ? "(recurring revenue)" : "(recurring expense)")}
                </div>
                {counterparty && (
                  <div className="text-[11px] text-white/50 truncate">
                    {counterpartyLabel}: {counterparty}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className="text-sm font-semibold tabular-nums"
                  style={{ color: isRev ? "#10B981" : "#F5F5F5" }}
                >
                  {isRev ? "+" : ""}{fmtMoney(u.amount, u.currency || "USD")}
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-white/30" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
